import {cloneMe, roundToTwo} from "../vendor/util";
import store from "../store/store";

/**
 * Ermittelt alle aktuell gültigen Rabatte für eine bestimmte POS-ID
 * @param {Array} discounts - Das Array aus store.getters.discounts
 * @param {String} posId - Die ID des Artikels
 * @param {String} currentUseCase - Der aktuelle Modus (z.B. "takeAway")
 * @returns {Array} - Liste der gültigen Rabatt-Objekte
 */
export function getValidDiscountsForArticle(discounts, posId, currentUseCase) {
  const now = new Date();

  return discounts.filter(discount => {
    if (!discount.conditionSet?.conditions?.length) {
      return false;
    }
    // --- NEU: Globaler Check ---
    // Wenn posArticles leer ist, gilt der Rabatt für alles.
    // Ansonsten muss die posId enthalten sein.
    const isGlobalDiscount = !discount.posArticles || discount.posArticles.length === 0;
    const isArticleSpecific = discount.posArticles && discount.posArticles.includes(posId);

    if (!isGlobalDiscount && !isArticleSpecific) return false;

    // --- Conditions prüfen (wie gehabt) ---
    const conditionCheck = (condition) => {
      if (!condition.isConditionActive) return true;

      // A. UseCase Check
      if (!condition.validForUseCases.includes(currentUseCase)) return false;

      // B. Zeitliche Bedingungen
      const tc = condition.timeCondition;
      const currentDay = now.toLocaleDateString('en-US', {weekday: 'short'}).toLowerCase();
      const currentHours = now.getHours();
      const currentMinutes = now.getMinutes();
      const todayISO = now.toISOString().split('T')[0];

      // Trigger-Logik
      switch (tc.triggerBy) {
        case "certainDate":
          if (todayISO !== tc.certainDate) return false;
          break;
        case "certainWeekDays":
          if (!tc.certainWeekDays.includes(currentDay)) return false;
          break;
        case "isAfterStartDate":
          if (todayISO < tc.isAfterStartDate) return false;
          break;
        case "isBeforeEndDate":
          if (todayISO > tc.isBeforeEndDate) return false;
          break;
      }

      // C. Uhrzeit-Check
      if (!tc.isFullDay && tc.validInTime) {
        // Umwandlung in Minuten seit Mitternacht für präzisen Vergleich
        const nowTotalMinutes = currentHours * 60 + currentMinutes;
        const fromTotalMinutes = tc.validInTime.fromHour * 60 + tc.validInTime.fromMinute;
        const tillTotalMinutes = tc.validInTime.tillHour * 60 + tc.validInTime.tillMinute;

        if (nowTotalMinutes < fromTotalMinutes || nowTotalMinutes > tillTotalMinutes) {
          return false;
        }
      }

      return true;
    };

    // Logische Verknüpfung der Conditions
    if (discount.conditionSet.logicalOperator === 'OR') {
      return discount.conditionSet.conditions.some(conditionCheck);
    } else {
      // Standardmäßig AND
      return discount.conditionSet.conditions.every(conditionCheck);
    }
  });
}

/**
 * Berechnet die absolute Ersparnis über den gesamten Warenkorb.
 * * @param {Object} cartMap - Map der Warenkorb-Artikel.
 * Struktur: { "unique_id_1": { posId: "34", itemTotal: 15.50 }, ... }
 * @param {Array} allDiscounts - Die Liste aus store.getters.discounts
 * @param {String} currentUseCase - z.B. "takeAway"
 * @returns {Number} - Die absolute Summe der Rabatte in Euro (z.B. 4.50)
 */
export const calculateTotalDiscountAbs = (_cartMap, _allDiscounts, currentUseCase) => {
  let totalSaved = 0;

  const cartMap = cloneMe(_cartMap);
  const allDiscounts = cloneMe(_allDiscounts);

  // Wir iterieren über jeden Artikel im Warenkorb
  Object.values(cartMap).forEach(item => {
    const validDiscounts = getValidDiscountsForArticle(allDiscounts, item.posId, currentUseCase);

    if (validDiscounts.length > 0) {
      // Summiere die Prozentsätze aller gültigen Rabatte für diesen Artikel
      const totalPercentage = validDiscounts.reduce((sum, d) => sum + d.percentage, 0);

      // Berechnung des Euro-Werts für diesen Artikel
      // Formel: (Einzelpreis * Gesamtprozent) / 100
      const itemSavings = (item.itemTotal * totalPercentage) / 100;

      totalSaved += itemSavings;
    }
  });

  // Rundung auf 2 Nachkommastellen, um Floating-Point-Fehler zu vermeiden
  return roundToTwo(totalSaved);
};

function getItemNameByPosId(posId, cartItems) {
  const item = cartItems.find(item => item.posId === posId);
  if (item) {
    return item.nameToDisplay;
  }
  return '';
}

/**
 * Erstellt eine detaillierte Liste aller angewendeten Rabatte.
 * @param {Object} cartMap - { "uid1": { posId: "34", itemTotal: 15.50 }, ... }
 * @param {Array} allDiscounts - store.getters.discounts
 * @param {String} currentUseCase - z.B. "takeAway"
 * @returns {Array} - Liste der angewendeten Rabatt-Details
 */
export const getAppliedDiscountsDetail = (_cartMap, _allDiscounts, currentUseCase) => {
  const appliedList = [];

  const cartMap = cloneMe(_cartMap);
  const allDiscounts = cloneMe(_allDiscounts);

  // Wir iterieren über jeden Artikel im Warenkorb
  Object.values(cartMap).forEach(item => {
    const validDiscounts = getValidDiscountsForArticle(allDiscounts, item.posId, currentUseCase);

    validDiscounts.forEach(discount => {
      // Berechnung des absoluten Betrags für diesen spezifischen Artikel
      const absoluteAmount = (item.itemTotal * discount.percentage) / 100;

      appliedList.push({
        posId: item.posId,
        itemName:  getItemNameByPosId(item.posId, cartMap),
        discountName: discount.name,
        percentage: discount.percentage,
        absoluteAmount: Math.round(absoluteAmount * 100) / 100,
        basePrice: item.itemTotal // Optional: zur Nachverfolgung, worauf berechnet wurde
      });
    });
  });

  return appliedList;
};
